'use client'

import * as React from 'react'
import { usePathname } from 'next/navigation'
import {
  ThemeProvider as NextThemesProvider,
  type ThemeProviderProps,
  useTheme,
} from 'next-themes'
import { isCustomLandingPageHostname } from '@/components/navbar-visibility'

// Customer-facing public segments (white-label landing pages: /p/[slug],
// /lp/domain/[domain]) must never inherit the app visitor's theme (a stored
// dark preference would leak onto tenant pages). `.dark` on
// <html> lets the APP stylesheet's class-scoped rules restyle tenant HTML
// (utility-name overlap + the Phase-1 contrast stopgap block), while the
// tenant page's own compiled CSS is media-based and expects no class at all.
// Forcing light here means the class is never stamped on these routes; tenant
// pages render on their own compiled CSS exactly as designed.
const TENANT_PUBLIC_ROUTE = /^\/(p|lp)(\/|$)/

function SystemThemeMigration() {
  const { theme, setTheme } = useTheme()
  const hasMigrated = React.useRef(false)

  React.useEffect(() => {
    if (hasMigrated.current || theme === undefined) return

    hasMigrated.current = true
    if (theme === 'system') {
      // enableSystem is off, so a stored 'system' value (pre-rebrand era) is
      // invalid — resolve it to the platform default (dark since 2026-09-11).
      // Explicit light/dark choices are untouched; for a signed-in user the
      // account preference then overrides whatever this browser remembered
      // (components/color-mode-sync.tsx).
      setTheme('dark')
    }
  }, [theme, setTheme])

  return null
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const pathname = usePathname()
  // Branded custom domains never match the route test: middleware REWRITES
  // pages.customer.com/<slug> to /lp/domain/<host>/<slug>, but usePathname()
  // reports the address-bar path (`/<slug>`) on both server and client. Detect
  // them by host instead — same primitive ConditionalNavbar uses. The check is
  // window-gated, so the SERVER-rendered theme script on those hosts is still
  // unforced; app/lp/domain/layout.tsx strips the dark class pre-paint to
  // cover that gap (host isn't readable here server-side without headers(),
  // which would cost every route its static rendering).
  const isTenantPublic =
    TENANT_PUBLIC_ROUTE.test(pathname ?? '') ||
    (typeof window !== 'undefined' && isCustomLandingPageHostname(window.location.hostname))

  // The marketing homepage is Slate & Ember dark by design (owner decision
  // 2026-07-21): forced, not defaulted — it has no theme toggle, and a
  // visitor's stored app preference shouldn't restyle the brand surface.
  // forcedTheme never writes storage, so the logged-in app default (dark)
  // and any remembered choice are untouched.
  const isMarketingHome = pathname === '/'

  return (
    <NextThemesProvider
      {...props}
      forcedTheme={isTenantPublic ? 'light' : isMarketingHome ? 'dark' : undefined}
    >
      {/* Migration writes to visitor storage — keep it off tenant pages. */}
      {isTenantPublic ? null : <SystemThemeMigration />}
      {children}
    </NextThemesProvider>
  )
}
